import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BranchState } from "../../../types";
import { axiosObj } from "../../../../config/apiClients";
import { Branch, BranchPayload } from "../../../../utils/types";

const initialState: BranchState = {
  isLoading: false,
  branches: [],
  branch: null,
  availableBranches: [{
    _id: '',
    branchName: '',
    branchLocation: '',
  }],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false
  }
}

export const getAllBranches = createAsyncThunk(
  "/esouq/branch/getAll",
  async ({ paramPage, paramLimit }: { paramPage: number; paramLimit: number }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`/esouq/branch/getAll?paramPage=${paramPage}&paramLimit=${paramLimit}`);

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Branches' });
    }
  }
);

export const getAvailableBranches = createAsyncThunk(
  "/esouq/branch/getAllIds",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/esouq/branch/getAllIds');

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Branches' });
    }
  }
);

export const createBranch = createAsyncThunk(
  "/esouq/branch/add",
  async (branch: BranchPayload, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/branch/create', branch);

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Add Branch' });
    }
  }
)

export const updateBranch = createAsyncThunk(
  "/esouq/branch/update",
  async (branch: BranchPayload, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/esouq/branch/update', branch);

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Branch' });
    }
  }
)

export const getBranch = createAsyncThunk(
  "/esouq/branch/getById",
  async (branchId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/branch/getById', { branchId });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Branch' });
    }
  }
)

export const deleteBranch = createAsyncThunk(
  "/esouq/branch/delete",
  async (branchId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete('/esouq/branch/delete', { data: { branchId } });

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Delete Branch' });
    }
  }
)

const branchesSlice = createSlice({
  name: "branches",
  initialState,
  reducers: {
    removeBranch: (state, action) => {
      const branchId = action.payload;
      state.branches = state.branches.filter((branch) => branch._id !== branchId);
    },
    setBranch: (state, action) => {
      state.branch = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getAllBranches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllBranches.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.payload.page === 1) {
          state.branches = action.payload.payload.allBranches;
        } else {
          const existingBranches = new Map(state.branches.map(p => [p._id, p]));
          action.payload.payload.allBranches.forEach((newBranch: Branch) => {
            if (!existingBranches.has(newBranch._id)) {
              state.branches.push(newBranch);
            }
          });
        }
        state.pagination = {
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage
        };
      })
      .addCase(getAllBranches.rejected, (state) => {
        state.isLoading = false;
        state.branches = [];
      })
      .addCase(getBranch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBranch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.branch = action.payload.payload;
      })
      .addCase(getBranch.rejected, (state) => {
        state.isLoading = false;
        state.branch = null;
      })
      .addCase(createBranch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createBranch.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createBranch.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateBranch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateBranch.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateBranch.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAvailableBranches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAvailableBranches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availableBranches = action.payload.payload.allBranches;
      })
      .addCase(getAvailableBranches.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setBranch, removeBranch } = branchesSlice.actions;

export default branchesSlice.reducer;